////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.5                                                            //
//  Summary: Layout shell for every public-facing route                     //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useOutlet } from 'react-router';

import { useReducedMotion } from '@/hooks/useReducedMotion';
import { crossfadeContent, reduceVariants } from '@/lib/motion';
import { SkipLink } from '@/components/chrome/SkipLink';
import { Header } from '@/components/chrome/Header';
import { Footer } from '@/components/chrome/Footer';
import { BackgroundLayer } from '@/components/chrome/BackgroundLayer';
import { SideKickMount } from '@/components/bot/SideKickMount';

// MARK — header height reserve. Header.tsx (1.0.3) is `position: fixed` and
// fully transparent (§3), so it no longer occupies flow space the way the
// 1.0.1 bordered header did. `pt-20` (5rem) is a tuned approximation of that
// header's rendered height (py-4 padding + an h-8 logo/40px burger button),
// same convention as typography.css's h1-huge clamp() comment — it stops a
// page's own <h1> sitting directly behind the burger/wordmark row on every
// route that has not yet grown its own full-viewport hero. Home's hero
// (components/content/HomeHero.tsx) cancels this with a matching `-mt-20` so it
// still reaches true viewport top rather than starting 5rem down.
export function PublicLayout () {
  const location = useLocation();
  const outlet = useOutlet();
  const reducedMotion = useReducedMotion();
  const variants = reduceVariants(crossfadeContent, reducedMotion);

  return (
    <div className="flex min-h-screen flex-col">
      {/* Outside <Outlet />, per 1.0.3.md §5 — one continuous, route-keyed layer
          that crossfades independently of the page-content transition below,
          coordinated only by both keying off the same route change (§6). */}
      <BackgroundLayer />
      <SkipLink />
      <Header />
      <main id="main-content" className="flex-1 pt-20">
        {/* `useOutlet()` (rather than `<Outlet />` directly) so the currently
            rendered route element can be handed to AnimatePresence as a keyed
            child — `createBrowserRouter`'s data router has no `<Routes>` to
            wrap, so this is the supported way to get a location-keyed
            transition out of it (React Router v6.4+/v7 pattern). */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {outlet}
          </motion.div>
        </AnimatePresence>
      </main>
      <Footer />
      <SideKickMount />
    </div>
  );
}
